"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { Text, SectionCard, CardTitle, CardDescription, Badge } from "@/src/components";
import { CodeBlock } from "./code-block";
import { PropsTable, type PropDefinition } from "./props-table";

export interface ExampleDefinition {
  title: string;
  description?: string;
  code: string;
  preview?: React.ReactNode;
}

export interface ComponentDocProps {
  /** Component name */
  name: string;
  /** Component description */
  description: string;
  /** Component status/badge */
  status?: "stable" | "beta" | "deprecated";
  /** Installation command (shadcn CLI) */
  installCommand?: string;
  /** Import path */
  importPath: string;
  /** Component import name(s) */
  importNames?: string[];
  /** Interactive playground */
  playground?: React.ReactNode;
  /** Prop definitions */
  props?: PropDefinition[];
  /** Usage examples */
  examples?: ExampleDefinition[];
  /** Accessibility guidelines */
  accessibility?: string[];
  /** Do guidelines */
  dos?: string[];
  /** Don't guidelines */
  donts?: string[];
  /** Additional sections */
  children?: React.ReactNode;
}

export function ComponentDoc({
  name,
  description,
  status = "stable",
  installCommand,
  importPath,
  importNames = [name],
  playground,
  props,
  examples,
  accessibility,
  dos,
  donts,
  children,
}: ComponentDocProps) {
  const importStatement = `import { ${importNames.join(", ")} } from "${importPath}"`;
  const defaultInstallCommand = `npx shadcn@latest add @bluecopa-ui/${name.toLowerCase()}`;

  return (
    <div className="space-y-12">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Text as="h1" className="text-h2 font-bold">
            {name}
          </Text>
          <Badge
            variant={
              status === "stable"
                ? "default"
                : status === "beta"
                ? "secondary"
                : "destructive"
            }
          >
            {status}
          </Badge>
        </div>
        <Text className="text-lg text-muted-foreground max-w-2xl">
          {description}
        </Text>
      </div>

      {/* Installation */}
      <SectionCard
        header={
          <>
            <CardTitle>Installation</CardTitle>
            <CardDescription>Add this component to your project</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        {/* CLI Installation */}
        <div className="space-y-2">
          <Text className="text-sm font-medium">Using CLI:</Text>
          <CodeBlock
            code={installCommand || defaultInstallCommand}
            language="bash"
          />
        </div>

        {/* Manual Import */}
        <div className="space-y-2">
          <Text className="text-sm font-medium">Import:</Text>
          <CodeBlock code={importStatement} language="tsx" />
        </div>
      </SectionCard>

      {/* Interactive Playground */}
      {playground && (
        <SectionCard
          header={
            <>
              <CardTitle>Playground</CardTitle>
              <CardDescription>
                Experiment with different configurations
              </CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          {playground}
        </SectionCard>
      )}

      {/* Examples */}
      {examples && examples.length > 0 && (
        <div className="space-y-6">
          <Text as="h2" className="text-h4 font-semibold">
            Examples
          </Text>
          {examples.map((example, i) => (
            <SectionCard
              key={i}
              header={
                <>
                  <CardTitle className="text-base">{example.title}</CardTitle>
                  {example.description && (
                    <CardDescription>{example.description}</CardDescription>
                  )}
                </>
              }
              contentClassName="p-0"
            >
              {/* Preview */}
              {example.preview && (
                <div className="p-6 border-b flex items-center justify-center min-h-32 bg-muted/20">
                  {example.preview}
                </div>
              )}
              {/* Code */}
              <CodeBlock
                code={example.code}
                language="tsx"
                className="border-0 rounded-none rounded-b-lg"
              />
            </SectionCard>
          ))}
        </div>
      )}

      {/* Props Table */}
      {props && props.length > 0 && <PropsTable props={props} />}

      {/* Accessibility */}
      {accessibility && accessibility.length > 0 && (
        <SectionCard
          header={<CardTitle>Accessibility</CardTitle>}
          contentClassName="p-6"
        >
          <ul className="space-y-2 text-sm">
            {accessibility.map((item, i) => (
              <li key={i}>• {item}</li>
            ))}
          </ul>
        </SectionCard>
      )}

      {/* Guidelines (Do's and Don'ts) */}
      {(dos || donts) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {dos && dos.length > 0 && (
            <SectionCard
              header={<CardTitle className="text-success">Do</CardTitle>}
              contentClassName="p-6"
            >
              <ul className="space-y-2 text-sm">
                {dos.map((item, i) => (
                  <li key={i} className="flex gap-2">
                    <div className="text-success">✓</div>
                    {item}
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}
          {donts && donts.length > 0 && (
            <SectionCard
              header={<CardTitle className="text-destructive">Don't</CardTitle>}
              contentClassName="p-6"
            >
              <ul className="space-y-2 text-sm">
                {donts.map((item, i) => (
                  <li key={i} className="flex gap-2">
                    <div className="text-destructive">✗</div>
                    {item}
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}
        </div>
      )}

      {/* Additional Content */}
      {children}
    </div>
  );
}

export default ComponentDoc;
